import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Star, BadgeCheck, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { REVIEWS_ALL, getReviewsFor } from '../data/reviews';
import { COMMUNITY_STORIES } from '../data/communityStories';
import { avatarThumb } from '../data/customerAvatars';
import AvatarWithFallback from './shared/AvatarWithFallback';

// Öffentliche Liste — Fallback, wenn kein Kontext übergeben wird.
export const REVIEWS = REVIEWS_ALL.slice(0, 4).map((r) => ({
  name: r.name, city: r.city, role: r.role, card: r.card, focus: r.focus,
  img: r.photo, text: r.text, short: r.short || r.text, rating: r.rating || 5,
}));

const Stars = ({ size = 'w-3 h-3' }) => (
  <span className="inline-flex items-center gap-0.5" aria-hidden="true">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} text-amber-400`} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

/**
 * MiniReviews — authentische Kundenrezensionen mit Portraits.
 * variant: 'cards' (editorial-Portrait auf Card, für Warenkorb-Bottom)
 *          | 'compact' (schmal, für Drawer / Configurator-Sidebar)
 * context: steuert welche Reviews gezeigt werden (jede Seite andere).
 * cartVariant: 'private' | 'business' — Filter nach Card-Variante.
 */
export default function MiniReviews({
  variant = 'cards',
  limit = 3,
  context,
  cartVariant,
  hideAvatar = false,
  testIdPrefix = 'mini-reviews',
  source = null,
}) {
  // ── STORY-VARIANT: nutzt IMMER die 11 authentischen Community-Portraits ──
  if (variant === 'story') {
    const stories = COMMUNITY_STORIES.slice(0, limit);
    return <StorySlider stories={stories} hideAvatar={hideAvatar} testIdPrefix={testIdPrefix} />;
  }

  // ── STORY-COMPACT: dieselben Konfigurator-Kundenstories, aber klein (z. B. Kasse) ──
  if (variant === 'story-compact') {
    const stories = COMMUNITY_STORIES.slice(0, limit);
    return <StoryCompactGrid stories={stories} hideAvatar={hideAvatar} testIdPrefix={testIdPrefix} />;
  }

  const rawSource = source
    ? source
    : context
      ? getReviewsFor(context, limit, cartVariant)
      : REVIEWS_ALL.slice(0, limit);
  const items = rawSource.slice(0, limit).map((r) => ({
    id: r.id,
    name: r.name,
    city: r.city,
    role: r.role.split('·')[0].trim(),
    card: r.card,
    focus: r.focus,
    img: r.photo,
    text: r.text,
    short: r.short || r.text,
    rating: r.rating || 5,
    age: r.age,
    usage: r.usage,
  }));

  /* ── SLIDER (Configurator: 1 sichtbar, auto-slide nach links) ── */
  if (variant === 'slider') {
    return <ReviewSlider items={items} hideAvatar={hideAvatar} testIdPrefix={testIdPrefix} />;
  }

  /* ── MARQUEE (Drawer: mehrere Rezensionen sliden endlos sanft nach links) ── */
  if (variant === 'marquee') {
    return <ReviewMarquee items={items} hideAvatar={hideAvatar} testIdPrefix={testIdPrefix} />;
  }

  /* ── COMPACT (Drawer / Sidebar) ── */
  if (variant === 'compact') {
    return (
      <div className="space-y-3" data-testid={testIdPrefix}>
        {items.map((r) => (
          <div
            key={r.id}
            className="rounded-2xl bg-stone-50 border border-stone-200 p-4"
            data-testid={`${testIdPrefix}-item-${r.id}`}
          >
            <div className="flex items-center gap-3">
              {!hideAvatar && (
                <AvatarWithFallback
                  src={avatarThumb(r.img, 96)}
                  name={r.name}
                  alt={`${r.name} — FuelX-Kunde aus ${r.city}`}
                  width="40"
                  height="40"
                  className="review-photo w-10 h-10 rounded-full object-cover flex-shrink-0"
                  initialsClassName="text-xs font-bold"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-stone-900 leading-tight">
                  {r.name} · <span className="text-stone-500 font-normal">{r.city}</span>
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Stars size="w-2.5 h-2.5" />
                  <span className="text-[10px] text-stone-500">{r.role}</span>
                </div>
              </div>
            </div>
            <p className="text-[12px] text-stone-600 leading-relaxed mt-2.5">
              &bdquo;{r.short}&ldquo;
            </p>
          </div>
        ))}
      </div>
    );
  }

  /* ── CARDS (editorial · Warenkorb-Bottom, Landing) ── */
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid={testIdPrefix}>
      {items.map((r) => (
        <article
          key={r.id}
          className="group relative bg-white border border-stone-200 rounded-2xl overflow-hidden flex flex-col hover:border-stone-900 hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] transition-all duration-500"
          data-testid={`${testIdPrefix}-item-${r.id}`}
        >
          {/* ── Portrait — editorial oben, nur wenn hideAvatar=false ── */}
          {!hideAvatar && (
            <div className="relative aspect-[5/4] bg-stone-100 overflow-hidden">
              <AvatarWithFallback
                src={r.img}
                name={r.name}
                alt={`Portrait von ${r.name} — FuelX-Kunde aus ${r.city}`}
                width="500"
                height="400"
                className="review-photo review-photo-large w-full h-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                initialsClassName="text-5xl font-semibold"
                loading="lazy"
                decoding="async"
              />
              {/* Verifiziert-Chip · oben links */}
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-0.5 rounded-full z-10">
                <BadgeCheck className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />
                <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-stone-700">
                  Verifiziert
                </span>
              </div>
              {/* Sanfter Verlauf für Lesbarkeit unten */}
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
              {/* Focus-Chip · unten links auf Gradient (kollidiert nicht mehr mit Verifiziert) */}
              {r.focus && (
                <div className="absolute bottom-3 left-3 right-3 z-10">
                  <div className="inline-flex items-center gap-1.5 bg-stone-950/90 backdrop-blur-sm px-2.5 py-1 rounded-full max-w-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" aria-hidden="true" />
                    <span className="text-[10px] font-medium tracking-tight text-emerald-300 truncate">
                      {r.focus}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Body ── */}
          <div className="flex flex-col flex-1 px-5 pt-4 pb-5">
            <div className="flex items-center justify-between mb-2.5">
              <Stars size="w-3 h-3" />
              <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-[0.16em] text-emerald-700">
                <BadgeCheck className="w-3 h-3" strokeWidth={2.5} />
                {hideAvatar ? 'Verifiziert' : r.card}
              </span>
            </div>

            {/* Focus-Chip inline (nur wenn Portrait ausgeblendet, sonst ist er im Portrait) */}
            {hideAvatar && r.focus && (
              <div className="mb-2.5">
                <span className="inline-flex items-center gap-1.5 bg-stone-100 px-2.5 py-1 rounded-full max-w-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[10px] font-medium tracking-tight text-stone-700 truncate">
                    {r.focus}
                  </span>
                </span>
              </div>
            )}

            {/* Quote-Text */}
            <div className="relative flex-1">
              <Quote
                className="absolute -top-1 -left-1 w-4 h-4 text-emerald-200"
                strokeWidth={2}
                fill="currentColor"
              />
              <p className="text-[13.5px] text-stone-700 leading-[1.6] pl-3 pt-0.5">
                {r.short}
              </p>
            </div>

            {/* Author */}
            <div className="mt-4 pt-3 border-t border-stone-100">
              <p className="text-[13px] font-semibold text-stone-900 leading-tight tracking-[-0.005em]">
                {r.name}
                {r.age && <span className="text-stone-500 font-normal"> · {r.age}</span>}
              </p>
              <p className="text-[11px] text-stone-500 mt-0.5 leading-snug">
                {r.role} · {r.city}
              </p>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * StoryCompactGrid — dieselben Konfigurator-Kundenstories, aber KLEIN.
 * Kompaktes 3er-Raster (Avatar-Thumb + Name + Sterne + kurzer Story-Text +
 * Ersparnis-Chip). Für die Kassenseite, damit Vertrauen ohne große Cards.
 * ═══════════════════════════════════════════════════════════════════════ */
function StoryCompactGrid({ stories, hideAvatar = false, testIdPrefix }) {
  if (!stories.length) return null;
  return (
    <div data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700 flex items-center gap-1.5">
          <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.2} />
          Echte Kundenstories
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {stories.map((s) => (
          <article
            key={s.id}
            className="rounded-2xl bg-white border border-stone-200 p-3.5 flex flex-col shadow-[0_4px_16px_-10px_rgba(15,23,42,0.2)]"
            data-testid={`${testIdPrefix}-item-${s.id}`}
          >
            <div className="flex items-center gap-2.5">
              {!hideAvatar && (
                <AvatarWithFallback
                  src={avatarThumb(s.photo, 96)}
                  name={s.name}
                  alt={`${s.name} — FuelX-Kunde aus ${s.city}`}
                  width="36"
                  height="36"
                  className="review-photo w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-white shadow-sm"
                  initialsClassName="text-[11px] font-bold"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-stone-900 leading-tight truncate">
                  {s.name} <span className="text-stone-400 font-normal">· {s.age}</span>
                </p>
                <p className="text-[10.5px] text-stone-500 leading-snug truncate">{s.role} · {s.city}</p>
              </div>
              <BadgeCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" strokeWidth={2.5} />
            </div>

            <div className="flex items-center gap-0.5 mt-2" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-3 h-3 ${i <= s.rating ? 'text-amber-400' : 'text-stone-200'}`} fill="currentColor" strokeWidth={0} />
              ))}
            </div>

            <p className="text-[12px] text-stone-600 leading-[1.55] mt-2 line-clamp-3 flex-1">
              &bdquo;{s.text}&ldquo;
            </p>

            {s.savings && (
              <div className="mt-2.5 pt-2.5 border-t border-stone-100">
                <span className="inline-flex items-center gap-1.5 max-w-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[10.5px] font-medium text-emerald-800 truncate">{s.savings} · {s.usp}</span>
                </span>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * ReviewSlider — zeigt 1 Rezension, MANUELLE Navigation (kein Auto-Advance).
 * V2 (Aug 2026): Auto-Play entfernt & feste Container-Höhe, damit die
 * gesamte Seite absolut statisch bleibt. Nutzer navigieren über Progress-Dots.
 * ═══════════════════════════════════════════════════════════════════════ */
function ReviewSlider({ items, hideAvatar = false, testIdPrefix }) {
  const [index, setIndex] = useState(0);

  if (items.length === 0) return null;
  const r = items[index];

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-stone-50 border border-stone-200"
      data-testid={testIdPrefix}
      style={{ minHeight: 168, height: 168 }}
    >
      <div
        className="p-4"
        data-testid={`${testIdPrefix}-item-${r.id}`}
      >
        <div className="flex items-center gap-3">
          {!hideAvatar && (
            <AvatarWithFallback
              src={avatarThumb(r.img, 96)}
              name={r.name}
              alt={`${r.name} — FuelX-Kunde aus ${r.city}`}
              width="44"
              height="44"
              className="review-photo w-11 h-11 rounded-full object-cover flex-shrink-0 ring-2 ring-white shadow-sm"
              initialsClassName="text-sm font-bold"
              loading="lazy"
              decoding="async"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-stone-900 leading-tight truncate">
              {r.name} · <span className="text-stone-500 font-normal">{r.city}</span>
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Stars size="w-2.5 h-2.5" />
              <span className="text-[10px] text-stone-500 truncate">{r.role}</span>
            </div>
          </div>
        </div>
        <p className="text-[12px] text-stone-600 leading-relaxed mt-2.5 line-clamp-3">
          &bdquo;{r.short}&ldquo;
        </p>
      </div>

      {/* Manuelle Navigation via Dots */}
      {items.length > 1 && (
        <div className="absolute bottom-2 right-3 flex items-center gap-1.5">
          {items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Rezension ${i + 1} von ${items.length} anzeigen`}
              className="p-2.5 -m-1 rounded-full flex items-center justify-center"
              data-testid={`${testIdPrefix}-dot-${i}`}
            >
              <span className={`block rounded-full transition-colors ${
                i === index
                  ? 'w-4 h-1.5 bg-stone-900'
                  : 'w-1.5 h-1.5 bg-stone-300 hover:bg-stone-500'
              }`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * ReviewMarquee — mehrere Rezensionen laufen endlos & sanft nach links.
 * Nahtlose Schleife durch doppelten Kartensatz; pausiert bei Hover.
 * ═══════════════════════════════════════════════════════════════════════ */
function ReviewMarquee({ items, hideAvatar = false, testIdPrefix }) {
  if (!items.length) return null;
  const loop = [...items, ...items];
  const dur = Math.max(28, items.length * 5);

  return (
    <div className="fx-review-marquee-wrap relative overflow-hidden" data-testid={testIdPrefix}>
      {/* Weiche Kanten-Verläufe links/rechts */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 z-10 bg-gradient-to-r from-white to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 z-10 bg-gradient-to-l from-white to-transparent" aria-hidden="true" />

      <div className="fx-review-marquee flex w-max py-1" style={{ '--fx-marquee-dur': `${dur}s` }}>
        {loop.map((r, i) => (
          <article
            key={`${r.id}-${i}`}
            className="mr-3 shrink-0 w-[278px] rounded-2xl bg-white border border-stone-200 shadow-[0_6px_24px_-12px_rgba(15,23,42,0.22)] p-4 flex flex-col"
            data-testid={i < items.length ? `${testIdPrefix}-item-${r.id}` : undefined}
            aria-hidden={i >= items.length}
          >
            <div className="flex items-center gap-2.5">
              {!hideAvatar && (
                <AvatarWithFallback
                  src={avatarThumb(r.img, 96)}
                  name={r.name}
                  alt={`${r.name} — FuelX-Kunde aus ${r.city}`}
                  width="40"
                  height="40"
                  className="review-photo w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-white shadow-sm"
                  initialsClassName="text-xs font-bold"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-stone-900 leading-tight truncate">{r.name}</p>
                <p className="text-[10.5px] text-stone-500 leading-snug truncate">{r.role} · {r.city}</p>
              </div>
              <BadgeCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" strokeWidth={2.5} />
            </div>

            <div className="flex items-center gap-2 mt-2.5">
              <Stars size="w-3 h-3" />
              <span className="text-[8.5px] font-medium uppercase tracking-[0.14em] text-stone-400">{r.card}</span>
            </div>

            <div className="relative mt-2 flex-1">
              <Quote className="absolute -top-0.5 -left-0.5 w-3.5 h-3.5 text-emerald-200" strokeWidth={2} fill="currentColor" />
              <p className="text-[12px] text-stone-600 leading-[1.55] pl-2.5 line-clamp-4">{r.short}</p>
            </div>

            {r.focus && (
              <div className="mt-3 pt-2.5 border-t border-stone-100">
                <span className="inline-flex items-center gap-1.5 max-w-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[10px] font-medium text-emerald-800 truncate">{r.focus}</span>
                </span>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * StorySlider — großformatiges Portrait + USP-Highlight + Storytelling.
 * V2 (Aug 2026): Auto-Play + Remount-Animation entfernt, damit die Seite
 * nicht mehr durch Story-Wechsel verschoben wird. Manuelle Navigation via Dots.
 * Feste Container-Höhe verhindert Reflow beim Wechsel zwischen Stories
 * unterschiedlicher Textlänge.
 * ═══════════════════════════════════════════════════════════════════════ */
function StorySlider({ stories, hideAvatar = false, testIdPrefix }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  // Alle Story-Portraits einmalig vorladen — kein Pop-in beim Wechsel
  useEffect(() => {
    if (stories.length <= 1) return;
    stories.forEach((s) => {
      const img = new window.Image();
      img.src = s.photo;
    });
  }, [stories]);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + stories.length) % stories.length);
  }, [stories.length]);

  // Sanftes Auto-Sliden alle 6 s — pausiert bei Hover/Touch; feste Höhe verhindert Layout-Shift
  useEffect(() => {
    if (stories.length <= 1 || paused) return undefined;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [stories.length, paused, go]);

  if (stories.length === 0) return null;
  const s = stories[index];

  // Swipe-Navigation für Mobile
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  return (
    <div
      className="relative"
      data-testid={testIdPrefix}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Header: Eyebrow */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700 flex items-center gap-2">
          <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.2} />
          Echte Kundenstories
        </p>
      </div>

      {/* Story-Card · V3: Slide-in-Animation (nur transform/opacity → kein Reflow) */}
      <article
        key={s.id}
        className="story-slide-in relative overflow-hidden rounded-3xl bg-white border border-stone-200 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-8px_rgba(0,0,0,0.08)] flex flex-col"
        style={{ minHeight: 420 }}
        data-testid={`${testIdPrefix}-item-${s.id}`}
      >
        {/* Portrait-Header mit Gradient-BG */}
        <div className="relative bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950 px-5 pt-6 pb-5 sm:px-7 sm:pt-8 sm:pb-6">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top_right,_rgba(62,255,126,0.35),_transparent_60%)]" aria-hidden="true" />

          <div className="relative flex items-start gap-4 sm:gap-5">
            {/* Portrait — GROß, mit Ring-Glow (nur wenn hideAvatar=false) */}
            {!hideAvatar && (
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-emerald-400/40 to-emerald-600/10 blur-md" aria-hidden="true" />
                <AvatarWithFallback
                  src={avatarThumb(s.photo, 224)}
                  name={s.name}
                  alt={`${s.name} · ${s.city}`}
                  className="relative w-[76px] h-[76px] sm:w-[112px] sm:h-[112px] rounded-full object-cover ring-2 ring-white/95 shadow-2xl"
                  initialsClassName="text-2xl sm:text-3xl font-semibold"
                  loading="lazy"
                  decoding="async"
                  width="112"
                  height="112"
                />
                {/* Verified-Badge */}
                <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full p-1 ring-2 ring-stone-950 shadow-lg">
                  <BadgeCheck className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </div>
              </div>
            )}

            <div className="min-w-0 flex-1 pt-1">
              {/* Rating */}
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`w-3.5 h-3.5 ${i <= s.rating ? 'text-amber-400' : 'text-white/20'}`} fill="currentColor" strokeWidth={0} />
                ))}
                <span className="text-[10px] text-white/50 tabular-nums ml-1">{s.rating}.0</span>
              </div>
              {/* Name + Alter */}
              <p className="text-white font-medium text-[15px] sm:text-base leading-tight tracking-[-0.005em]">
                {s.name} <span className="text-white/50 font-normal whitespace-nowrap">· {s.age}</span>
              </p>
              {/* Role · Stadt */}
              <p className="text-white/60 text-[11px] sm:text-xs mt-0.5 leading-snug truncate">
                {s.role} · {s.city}
              </p>
            </div>
          </div>

          {/* USP-Preis-Badge — volle Breite unter dem Kopf, damit mobil nichts abgeschnitten wird */}
          <div className="relative mt-3.5 flex min-w-0">
            <div className="inline-flex items-center gap-1.5 max-w-full min-w-0 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 backdrop-blur-sm">
              <span className="text-emerald-300 font-semibold text-[11px] sm:text-xs tabular-nums whitespace-nowrap">{s.savings}</span>
              <span className="text-white/80 text-[10px] sm:text-[11px] truncate">· {s.usp.replace(s.savings, '').replace(/^\s*[€\s]+/, '').trim()}</span>
            </div>
          </div>
        </div>

        {/* Story-Body: Text + Quote — line-clamp verhindert Höhen-Sprünge */}
        <div className="relative px-5 sm:px-7 py-5 sm:py-6 flex-1">
          <Quote className="absolute top-4 right-5 w-8 h-8 text-emerald-100" strokeWidth={1.5} aria-hidden="true" />
          <p className="text-stone-700 text-[13px] sm:text-sm leading-[1.6] tracking-[-0.003em] relative line-clamp-6 sm:line-clamp-none">
            {s.text}
          </p>
        </div>
      </article>

      {/* Pfeil-Navigation — ersetzt die Dots, mobil-freundliche 44px-Targets */}
      {stories.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            type="button"
            onClick={() => { setPaused(true); go(-1); }}
            aria-label="Vorherige Kundenstory"
            className="w-11 h-11 rounded-full border border-stone-200 bg-white text-stone-600 flex items-center justify-center shadow-sm hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 active:scale-95 transition-[color,background-color,border-color,transform]"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2.2} />
          </button>
          <p className="text-[11px] text-stone-500 tabular-nums min-w-[48px] text-center select-none" data-testid={`${testIdPrefix}-counter`}>
            {index + 1} / {stories.length}
          </p>
          <button
            type="button"
            onClick={() => { setPaused(true); go(1); }}
            aria-label="Nächste Kundenstory"
            className="w-11 h-11 rounded-full border border-stone-200 bg-white text-stone-600 flex items-center justify-center shadow-sm hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 active:scale-95 transition-[color,background-color,border-color,transform]"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2.2} />
          </button>
        </div>
      )}
    </div>
  );
}
